// Logto 权限工具函数
import { ReservedResource, UserScope } from '@logto/react';
import { API_RESOURCE, APP_SCOPE, ORGANIZATION_PERMISSIONS } from './logtoConfig';

/**
 * 解析 JWT Token 的 payload
 * @param {string} token - JWT Token
 * @returns {object|null} - Token payload
 */
export function parseJwtPayload(token) {
  if (!token) {
    return null;
  }

  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('解析 JWT 失败:', error);
    return null;
  }
}

/**
 * 获取组织权限（非API）
 * @param {object} logtoClient - Logto客户端实例
 * @param {string} organizationId - 组织ID
 * @returns {Promise<string[]>} - 权限列表
 */
export async function getOrganizationPermissions(logtoClient, organizationId) {
  try {
    let orgToken = null;
    
    // 尝试使用 getOrganizationToken（如果可用）
    if (logtoClient.getOrganizationToken && typeof logtoClient.getOrganizationToken === 'function') {
      try {
        orgToken = await logtoClient.getOrganizationToken(organizationId);
      } catch (error) {
        // 回退到 getAccessToken
        orgToken = await logtoClient.getAccessToken(ReservedResource.Organization, organizationId);
      }
    } else {
      // 使用 getAccessToken
      orgToken = await logtoClient.getAccessToken(ReservedResource.Organization, organizationId);
    }

    if (!orgToken) {
      return [];
    }

    const claims = parseJwtPayload(orgToken);
    
    // 验证 Token 结构
    const expectedAud = `urn:logto:resource:organizations`;
    if (claims?.aud !== expectedAud) {
      console.warn('Token audience 不匹配');
    }

    // 解析权限
    const scopes = claims?.scope ? claims.scope.split(' ').filter(Boolean) : [];
    return scopes;
  } catch (error) {
    console.error('获取组织权限失败:', error);
    return [];
  }
}

/**
 * 获取组织级 API 资源权限
 * @param {object} logtoClient - Logto客户端实例
 * @param {string} organizationId - 组织ID
 * @param {string[]} apiResources - API资源列表
 * @returns {Promise<Map<string, string[]>>} - 资源权限映射
 */
export async function getApiResourcePermissions(logtoClient, organizationId, apiResources) {
  const permissionsMap = new Map();

  await Promise.all(
    apiResources.map(async (resource) => {
      try {
        // 使用 getAccessToken(resource, organizationId) 获取组织级 API 资源 Token
        const token = await logtoClient.getAccessToken(resource, organizationId);

        if (!token) {
          console.log(`⚠️ [${resource}] 未获取到 Token`);
          return;
        }

        const claims = parseJwtPayload(token);

        // 验证 Token 结构
        if (claims?.aud !== resource) {
          console.warn(`⚠️ [${resource}] Token audience 不匹配`);
        }

        if (claims?.organization_id !== organizationId) {
          console.warn(`⚠️ [${resource}] Organization ID 不匹配`);
        }

        // 解析权限
        const scopes = claims?.scope ? claims.scope.split(' ').filter(Boolean) : [];
        if (scopes.length > 0) {
          permissionsMap.set(resource, scopes);
        }
      } catch (error) {
        console.warn(`❌ [${resource}] 获取 Token 失败:`, error);
      }
    })
  );

  return permissionsMap;
}

/**
 * 加载所有权限信息
 * @param {object} logtoClient - Logto客户端实例
 * @returns {Promise<object>} - 权限信息对象
 */
export async function loadAllPermissions(logtoClient) {
  const isAuthenticated = await logtoClient.isAuthenticated();
  if (!isAuthenticated) {
    return {
      userInfo: null,
      organizationScopes: [],
      apiResourceScopes: new Map(),
      allScopes: [],
      organizationId: null,
    };
  }

  try {
    // 1. 获取ID Token Claims以获取组织列表
    let idTokenClaims = null;
    try {
      idTokenClaims = await logtoClient.getIdTokenClaims();
    } catch (error) {
      console.warn('获取ID Token Claims失败:', error);
    }
    
    // 从ID Token获取组织列表
    const organizations = idTokenClaims?.organizations || [];
    
    // 获取用户信息
    const userInfo = await logtoClient.fetchUserInfo();
    const organizationData = userInfo?.organization_data || [];
    
    // 优先使用ID Token中的组织列表，如果没有则使用userInfo中的
    const finalOrganizations = organizations.length > 0 ? organizations : organizationData;

    const organizationScopesSet = new Set();
    const allScopesSet = new Set();
    let apiResourceScopesMap = new Map();

    // 如果用户属于组织，获取组织权限
    if (finalOrganizations.length > 0) {
      // 选组织ID：默认取第一个
      const organizationId = typeof finalOrganizations[0] === 'string' 
        ? finalOrganizations[0] 
        : finalOrganizations[0].id;

      // 1. 获取组织权限（非API）
      const orgScopes = await getOrganizationPermissions(logtoClient, organizationId);
      orgScopes.forEach((scope) => {
        organizationScopesSet.add(scope);
        allScopesSet.add(scope);
      });

      // 2. 获取组织级 API 资源权限
      const apiResources = [API_RESOURCE];
      apiResourceScopesMap = await getApiResourcePermissions(logtoClient, organizationId, apiResources);

      // 合并所有权限
      apiResourceScopesMap.forEach((scopes) => {
        scopes.forEach((scope) => allScopesSet.add(scope));
      });

      // 打印权限信息（格式化输出）
      console.log('%c═══════════════════════════════════════════════════════', 'color: #667eea; font-weight: bold; font-size: 14px;');
      console.log('%c📋 Logto 权限信息', 'color: #667eea; font-weight: bold; font-size: 16px;');
      console.log('%c═══════════════════════════════════════════════════════', 'color: #667eea; font-weight: bold; font-size: 14px;');

      // 用户身份信息
      console.group('%c👤 用户身份信息', 'color: #4CAF50; font-weight: bold;');
      const userId = idTokenClaims?.sub || userInfo?.sub || userInfo?.id || '(未获取)';
      console.log('用户ID:', userId);
      console.log('用户名:', userInfo.username || '(未设置)');
      console.log('姓名:', userInfo.name || '(未设置)');
      console.log('邮箱:', userInfo.primaryEmail || userInfo.email || '(未设置)');
      if (finalOrganizations.length > 0) {
        console.log('所属组织:');
        finalOrganizations.forEach((org, index) => {
          if (typeof org === 'string') {
            console.log(`  ${index + 1}. ${org} (ID: ${org})`);
          } else {
            console.log(`  ${index + 1}. ${org.name || org.id} (ID: ${org.id})`);
          }
        });
      } else {
        console.log('所属组织: 无');
      }
      console.groupEnd();

      // 组织权限（非API）
      console.group('%c🏢 组织权限（非API）', 'color: #2196F3; font-weight: bold;');
      if (organizationScopesSet.size > 0) {
        Array.from(organizationScopesSet).forEach((scope, index) => {
          console.log(`${index + 1}. ${scope}`);
        });
      } else {
        console.log('无组织权限');
      }
      console.log('权限总数:', organizationScopesSet.size);
      console.groupEnd();

      // API资源权限（组织级）
      console.group('%c🌐 组织级API资源权限', 'color: #FF9800; font-weight: bold;');
      if (apiResourceScopesMap.size > 0) {
        apiResourceScopesMap.forEach((scopes, resource) => {
          console.group(`📡 ${resource}`);
          if (scopes.length > 0) {
            scopes.forEach((scope, index) => {
              console.log(`${index + 1}. ${scope}`);
            });
          } else {
            console.log('无权限');
          }
          console.log('权限数量:', scopes.length);
          console.groupEnd();
        });
      } else {
        console.log('无API资源权限');
      }
      console.log('API资源总数:', apiResourceScopesMap.size);
      console.groupEnd();

      // 所有权限汇总
      console.group('%c📊 权限汇总', 'color: #9C27B0; font-weight: bold;');
      console.log('所有权限列表:', Array.from(allScopesSet));
      console.log('总权限数:', allScopesSet.size);
      console.log(`是否有 ${APP_SCOPE} 权限:`, allScopesSet.has(APP_SCOPE) ? '✅ 是' : '❌ 否');
      console.groupEnd();

      console.log('%c═══════════════════════════════════════════════════════', 'color: #667eea; font-weight: bold; font-size: 14px;');

      return {
        userInfo,
        organizationScopes: Array.from(organizationScopesSet),
        apiResourceScopes: apiResourceScopesMap,
        allScopes: Array.from(allScopesSet),
        organizationId: organizationId,
      };
    } else {
      // 用户不属于任何组织，尝试获取用户级别的权限
      console.log('%c⚠️ 用户不属于任何组织', 'color: #FF9800; font-weight: bold;');
      
      return {
        userInfo,
        organizationScopes: [],
        apiResourceScopes: new Map(),
        allScopes: [],
        organizationId: null,
      };
    }
  } catch (error) {
    console.error('获取权限失败:', error);
    return {
      userInfo: null,
      organizationScopes: [],
      apiResourceScopes: new Map(),
      allScopes: [],
      organizationId: null,
    };
  }
}
